import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Fullscreen for a DOM subtree, with a CSS fallback.
 *
 * The native Fullscreen API is preferred (it hides the browser chrome
 * too), but it does not exist on iOS Safari for anything that isn't a
 * <video>, older WebKit reports failure through an event instead of a
 * rejected promise, and any browser may refuse a request that isn't
 * tied to a user gesture. In all of those cases the caller pins the
 * element over the viewport instead — see `isFullscreen` below, which
 * the caller MUST translate into `position: fixed; inset: 0` styling.
 * Browsers' `:fullscreen` UA stylesheets do the same thing natively, so
 * one class string covers both modes.
 *
 * Whatever you attach the ref to becomes the fullscreen root: in native
 * mode the rest of the document is not rendered at all, so anything that
 * must stay visible (overlays, drawers) has to live inside it.
 */

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

function currentFullscreenElement(): Element | null {
  const d = document as FullscreenDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

function isThenable(v: unknown): v is Promise<unknown> {
  return typeof (v as Promise<unknown> | undefined)?.then === 'function';
}

export function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const [native, setNative] = useState(false);
  const [fallback, setFallback] = useState(false);
  /** Set while a native request is in flight, so a second click during
   *  the round trip can't fire a competing request (the browser rejects
   *  one of the pair, which used to strand the hook in fallback mode). */
  const pending = useRef(false);

  // Track native fullscreen from the document — the user can leave it
  // with Escape or the browser's own UI, never touching our button.
  useEffect(() => {
    const sync = () => {
      const on = currentFullscreenElement() === ref.current;
      pending.current = false;
      setNative(on);
      // Native wins: never leave both modes on at once.
      if (on) setFallback(false);
    };
    const onError = () => {
      // Any element on the page can fail a request; only ours counts.
      if (!pending.current) return;
      pending.current = false;
      // WebKit reports a refused request here rather than by rejecting.
      if (ref.current?.isConnected) setFallback(true);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    document.addEventListener('fullscreenerror', onError);
    document.addEventListener('webkitfullscreenerror', onError);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      document.removeEventListener('fullscreenerror', onError);
      document.removeEventListener('webkitfullscreenerror', onError);
    };
  }, [ref]);

  // Escape leaves the CSS fallback; the browser already handles it for
  // native fullscreen. A drawer inside the map may claim the key first
  // by calling preventDefault — but it listens on `window` too, so
  // whether its handler has run yet depends on registration order.
  // Reading `defaultPrevented` on the next task instead of inline means
  // the whole dispatch has finished and the answer is final.
  useEffect(() => {
    if (!fallback) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      timer = setTimeout(() => {
        if (!e.defaultPrevented) setFallback(false);
      }, 0);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (timer) clearTimeout(timer);
    };
  }, [fallback]);

  const toggle = useCallback(() => {
    const el = ref.current as FullscreenElement | null;
    if (!el || pending.current) return;

    if (currentFullscreenElement() === el) {
      const d = document as FullscreenDocument;
      const result = d.exitFullscreen?.() ?? d.webkitExitFullscreen?.();
      if (isThenable(result)) result.catch(() => undefined);
      return;
    }
    if (fallback) {
      setFallback(false);
      return;
    }

    const request = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    if (!request) {
      setFallback(true);
      return;
    }

    pending.current = true;
    try {
      const result = request();
      // Legacy WebKit returns undefined and signals failure through
      // `webkitfullscreenerror`, handled by the listener above.
      if (isThenable(result)) {
        result.catch(() => {
          pending.current = false;
          if (ref.current?.isConnected) setFallback(true);
        });
      }
    } catch {
      // Old Safari throws synchronously instead of rejecting.
      pending.current = false;
      setFallback(true);
    }
  }, [fallback, ref]);

  return { isFullscreen: native || fallback, toggle };
}
